import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { styled } from "@mui/system";
import { COLORS, GAPS } from "../../common/consts";
import { SettingsCaption, SettingsControlField, SettingsFooter, SettingsGroup, SettingsHeader, SettingsHeaderSkeleton, SettingsPanel, SettingsRow, SettingsRowSkeleton, SettingsTitle } from "../Settings";
import { BlockBone, TextBone } from "../Display/Skeletons";
import Button, { ButtonVariants } from "../Display/Button";
import Text, { TextVariant } from "../Display/Text";
import Toggle from "../DataInput/Toggle";
import Select from "../DataInput/Select";
import SearchableSelect from "../DataInput/SearchableSelect";
import NumberInput from "../DataInput/NumberInput";
import { getDialerSettings, updateDialerSettings } from "../../services/firebase";
import PlanUpgradePrompt from "../Billing/PlanUpgradePrompt";
import { isPlanRequiredError, planRequiredMessage } from "../../common/plans";
import {
  DIALER_DISPOSITION_KEYS,
  DIALER_DISPOSITION_LABELS,
  DialerDispositionKey,
  DialerSettings,
} from "../../types/dialer";
import { useCrmName } from "../CrmUi/useCrm";

const Grid = styled("div")`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${GAPS.sm} ${GAPS.md};
  padding: 0.75rem 0 1rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
  gap: ${GAPS.xs};
`;

const ErrorText = styled(Text)`
  color: var(--color-integrationDisconnected, ${COLORS.integrationDisconnected});
`;

type Status = "loading" | "ready" | "saving" | "error";

/**
 * Org-wide dialer configuration for the boring extension: which Outreach
 * disposition each call outcome logs, plus behavior defaults. Saved to the
 * extension config the extension reads on startup.
 */
const DialerSection: FC = () => {
  const [settings, setSettings] = useState<DialerSettings | null>(null);
  const [saved, setSaved] = useState<DialerSettings | null>(null);
  const [outreachDispositions, setOutreachDispositions] = useState<string[] | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<string | null>(null);
  const [planRequired, setPlanRequired] = useState(false);
  const crmName = useCrmName();

  const load = useCallback(async () => {
    setStatus("loading");
    setError(null);
    try {
      const result = await getDialerSettings();
      setSettings(result.settings);
      setSaved(result.settings);
      setOutreachDispositions(result.outreachDispositions);
      setPlanRequired(!!result.planRequired);
      setStatus("ready");
    } catch (err) {
      console.error("Failed to load dialer settings", err);
      setError("Could not load dialer settings. You need to be an organization admin.");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const dirty = useMemo(
    () => !!settings && !!saved && JSON.stringify(settings) !== JSON.stringify(saved),
    [settings, saved]
  );

  const options = useMemo(() => {
    const fromOrg = settings?.availableDispositions || [];
    const fromApi = outreachDispositions || [];
    const chosen = settings ? Object.values(settings.dispositions) : [];
    return Array.from(new Set([...fromApi, ...fromOrg, ...chosen])).sort();
  }, [settings, outreachDispositions]);

  const source = settings?.availableDispositionsSource;
  const sourceHint =
    source === "outreach_api"
      ? "Disposition names come from your Outreach org."
      : source === "extension"
      ? `Disposition names were reported by the extension from the ${crmName} dialer.`
      : `Type the disposition names exactly as they appear in ${crmName}. The extension will report the real list after the first call.`;

  const update = (patch: Partial<DialerSettings>) =>
    setSettings((current) => (current ? { ...current, ...patch } : current));

  const save = async () => {
    if (!settings) return;
    setStatus("saving");
    setError(null);
    try {
      const result = await updateDialerSettings(settings);
      setSettings(result.settings);
      setSaved(result.settings);
      setStatus("ready");
    } catch (err) {
      console.error("Failed to save dialer settings", err);
      setError(isPlanRequiredError(err) ? planRequiredMessage("dialer") : "Could not save dialer settings.");
      setStatus("ready");
    }
  };

  if ((status === "loading" || !settings) && error) {
    return (
      <SettingsPanel>
        <SettingsCaption>{error}</SettingsCaption>
      </SettingsPanel>
    );
  }

  if (status === "loading" || !settings) {
    /* The panel as it comes back: the heading with its organization switch,
       the call outcome fields two to a row, then the behaviour switches. */
    return (
      <SettingsPanel>
        <SettingsHeaderSkeleton titleWidth={72} captionWidths={["58%"]} control="toggle" />

        <SettingsGroup>
          <div>
            <SettingsRowSkeleton labelWidth={140} captionWidth="66%" control="none" />
            <Grid>
              {DIALER_DISPOSITION_KEYS.map((key: DialerDispositionKey) => (
                <Field key={key}>
                  <BlockBone height={40} />
                  <TextBone width="72%" size="0.75rem" />
                </Field>
              ))}
            </Grid>
          </div>

          <SettingsRowSkeleton labelWidth={110} captionWidth="70%" control="none" />
          <SettingsRowSkeleton labelWidth={250} captionWidth="62%" control="toggle" />
          <SettingsRowSkeleton labelWidth={270} captionWidth="58%" control="toggle" />
          <SettingsRowSkeleton labelWidth={290} control="toggle" />
          <SettingsRowSkeleton labelWidth={210} captionWidth="54%" control="toggle" />
          <SettingsRowSkeleton labelWidth={180} control="toggle" />
        </SettingsGroup>

        <SettingsFooter>
          <BlockBone width={96} height={36} radius="9999px" />
          <BlockBone width={168} height={36} radius="9999px" />
        </SettingsFooter>
      </SettingsPanel>
    );
  }

  return (
    <SettingsPanel>
      <SettingsHeader
        title="Dialer"
        caption="Turns the dialer on for licensed users in this organization."
        control={<Toggle checked={settings.enabled} disabled={planRequired} onChange={(enabled) => update({ enabled })} />}
      />
      {planRequired && <PlanUpgradePrompt feature="dialer" what="Dial faster" />}

      <SettingsGroup>
        <div>
          <SettingsRow label={<SettingsTitle>Call outcomes</SettingsTitle>} caption={sourceHint} />
          <Grid>
            {DIALER_DISPOSITION_KEYS.map((key: DialerDispositionKey) => (
              <Field key={key}>
                <SearchableSelect
                  freeSolo
                  label={DIALER_DISPOSITION_LABELS[key].title}
                  options={options.map((option) => ({ value: option, label: option }))}
                  value={settings.dispositions[key]}
                  onChange={(value) =>
                    update({ dispositions: { ...settings.dispositions, [key]: (value || "").toString() } })
                  }
                />
                <SettingsCaption style={{ fontSize: "0.75rem", lineHeight: "1rem" }}>{DIALER_DISPOSITION_LABELS[key].hint}</SettingsCaption>
              </Field>
            ))}
          </Grid>
        </div>

        <SettingsRow label={<SettingsTitle>Behavior</SettingsTitle>} caption="Defaults for every rep. Each person can change these for themselves." />
        <SettingsRow
          label="Handle voicemail automatically"
          caption="Detects the beep and acts without the rep listening for it."
          control={
            <Toggle
              checked={settings.flags.AUTOMATED_VOICEMAIL_FLOW}
              onChange={(v) => update({ flags: { ...settings.flags, AUTOMATED_VOICEMAIL_FLOW: v } })}
            />
          }
        />
        {settings.flags.AUTOMATED_VOICEMAIL_FLOW && (
          <SettingsRow
            label="When a voicemail is detected"
            control={
              <SettingsControlField>
                <Select
                  value={settings.flags.AUTOMATED_VOICEMAIL_BEHAVIOR}
                  onChange={(e: any) =>
                    update({ flags: { ...settings.flags, AUTOMATED_VOICEMAIL_BEHAVIOR: e?.target?.value || e } })
                  }
                  options={[
                    { value: "SKIP", label: "Hang up and log no answer" },
                    { value: "DROP", label: "Drop the recorded voicemail" },
                  ]}
                />
              </SettingsControlField>
            }
          />
        )}
        <SettingsRow
          label="Handle gatekeepers automatically"
          caption="Recognises a switchboard and logs the outcome."
          control={
            <Toggle
              checked={settings.flags.AUTOMATED_GATEKEEPER_FLOW}
              onChange={(v) => update({ flags: { ...settings.flags, AUTOMATED_GATEKEEPER_FLOW: v } })}
            />
          }
        />
        <SettingsRow
          label="Redial calls that drop while ringing"
          control={
            <Toggle
              checked={settings.flags.EARLY_CALL_END_REDIAL}
              onChange={(v) => update({ flags: { ...settings.flags, EARLY_CALL_END_REDIAL: v } })}
            />
          }
        />
        <SettingsRow
          label="Lock hang up while ringing"
          control={
            <Toggle
              checked={settings.flags.LOCK_HANGUP_WHILE_RINGING}
              onChange={(v) => update({ flags: { ...settings.flags, LOCK_HANGUP_WHILE_RINGING: v } })}
            />
          }
        />
        <SettingsRow
          label="Ask for a number when the prospect has none"
          control={
            <Toggle
              checked={settings.flags.NO_NUMBER_ENRICHMENT}
              onChange={(v) => update({ flags: { ...settings.flags, NO_NUMBER_ENRICHMENT: v } })}
            />
          }
        />
        <SettingsRow
          label="Hang up unanswered calls"
          caption="Ends the call once it has rung for this long."
          control={
            <Toggle
              checked={settings.flags.RING_TIMEOUT_ENABLED}
              onChange={(v) => update({ flags: { ...settings.flags, RING_TIMEOUT_ENABLED: v } })}
            />
          }
        >
          {settings.flags.RING_TIMEOUT_ENABLED && (
            <NumberInput
              label="Seconds"
              value={settings.flags.RING_TIMEOUT_SECONDS}
              onChange={(v) => update({ flags: { ...settings.flags, RING_TIMEOUT_SECONDS: v } })}
            />
          )}
        </SettingsRow>

        <SettingsRow label={<SettingsTitle>Delay between calls</SettingsTitle>} />
        <SettingsRow
          label="Wait before dialing the next number"
          caption="A breath between dials."
          control={
            <Toggle
              checked={settings.callDelay.enabled}
              onChange={(v) => update({ callDelay: { ...settings.callDelay, enabled: v } })}
            />
          }
        >
          {settings.callDelay.enabled && (
            <NumberInput
              label="Seconds"
              value={Math.round(settings.callDelay.delayMs / 1000)}
              onChange={(v) => update({ callDelay: { ...settings.callDelay, delayMs: Math.max(0, v) * 1000 } })}
            />
          )}
        </SettingsRow>
        <SettingsRow
          label="Lock these values"
          caption="Reps cannot change the delay for themselves."
          control={
            <Toggle
              checked={settings.callDelay.adminLocked}
              onChange={(v) => update({ callDelay: { ...settings.callDelay, adminLocked: v } })}
            />
          }
        />

        <SettingsRow label={<SettingsTitle>Calling hours</SettingsTitle>} />
        <SettingsRow
          label="Restrict calling by the prospect's local time"
          control={
            <Toggle
              checked={settings.callingHours.enabled}
              onChange={(v) => update({ callingHours: { ...settings.callingHours, enabled: v } })}
            />
          }
        >
          {settings.callingHours.enabled && (
            <div style={{ display: "flex", gap: "0.75rem" }}>
              <NumberInput
                label="From (hour)"
                value={settings.callingHours.minHour}
                onChange={(v) => update({ callingHours: { ...settings.callingHours, minHour: v } })}
              />
              <NumberInput
                label="To (hour)"
                value={settings.callingHours.maxHour}
                onChange={(v) => update({ callingHours: { ...settings.callingHours, maxHour: v } })}
              />
            </div>
          )}
        </SettingsRow>
        {settings.callingHours.enabled && (
          <SettingsRow
            label="Window mode"
            control={
              <SettingsControlField>
                <Select
                  value={settings.callingHours.blockMode ? "block" : "allow"}
                  onChange={(e: any) =>
                    update({ callingHours: { ...settings.callingHours, blockMode: (e?.target?.value || e) === "block" } })
                  }
                  options={[
                    { value: "allow", label: "Only call inside the window" },
                    { value: "block", label: "Never call inside the window" },
                  ]}
                />
              </SettingsControlField>
            }
          />
        )}
      </SettingsGroup>

      {error && <ErrorText variant={TextVariant.sm}>{error}</ErrorText>}
      <SettingsFooter>
        <Button
          variant={ButtonVariants.light}
          type="button"
          onClick={() => saved && setSettings(saved)}
          disabled={!dirty || status === "saving"}
        >
          Discard
        </Button>
        <Button
          variant={ButtonVariants.brand}
          type="button"
          onClick={save}
          disabled={!dirty || status === "saving"}
          loading={status === "saving"}
        >
          Save dialer settings
        </Button>
      </SettingsFooter>
    </SettingsPanel>
  );
};

export default DialerSection;
